/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ResolveIdentityRequest,
  ResolveIdentitySecurity,
} from "../models/operations/resolveidentity.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildIdentityQuery,
  IdentityQueryData,
  prefetchIdentity,
  queryKeyIdentity,
} from "./identity.core.js";
export {
  buildIdentityQuery,
  type IdentityQueryData,
  prefetchIdentity,
  queryKeyIdentity,
};

export type IdentityQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * resolve identity
 *
 * @remarks
 * Resolve an identity URN into every identifier the subject's activity is recorded under.
 */
export function useIdentity(
  request: ResolveIdentityRequest,
  security?: ResolveIdentitySecurity | undefined,
  options?: QueryHookOptions<IdentityQueryData, IdentityQueryError>,
): UseQueryResult<IdentityQueryData, IdentityQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildIdentityQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * resolve identity
 *
 * @remarks
 * Resolve an identity URN into every identifier the subject's activity is recorded under.
 */
export function useIdentitySuspense(
  request: ResolveIdentityRequest,
  security?: ResolveIdentitySecurity | undefined,
  options?: SuspenseQueryHookOptions<IdentityQueryData, IdentityQueryError>,
): UseSuspenseQueryResult<IdentityQueryData, IdentityQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildIdentityQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setIdentityData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      urn: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: IdentityQueryData,
): IdentityQueryData | undefined {
  const key = queryKeyIdentity(...queryKeyBase);

  return client.setQueryData<IdentityQueryData>(key, data);
}

export function invalidateIdentity(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      urn: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "identity", "resolve", ...queryKeyBase],
  });
}

export function invalidateAllIdentity(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "identity", "resolve"],
  });
}
